import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  useToast,
} from '@chakra-ui/react';

import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import { useGlobalContext } from '../state/GlobalContext';
import { addDiscount } from '../api';
import styled from 'styled-components';
import { getSubscriptionLevelDiscount } from '../utils';

const DiscountAvailableModal: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    subscriptionId,
    customerId,
    authToken,
    customerPortalSettings,
  } = useGlobalContext();
  const {
    loadPreviousFlowStep,
    onClose,
    loadNextFlowStep,
  } = useModalManagerContext();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const callAddDiscount = ({ customerId, subscriptionId, authToken }) => {
    setIsLoading(true);

    return addDiscount(customerId, subscriptionId, authToken);
  };

  const onAddDiscountMutationSuccess = async () => {
    const previousSubscriptionValue = queryClient.getQueryData<Subscription>([
      'subscription',
      customerId,
      subscriptionId,
    ]);
    setIsLoading(false);

    queryClient.setQueryData(
      ['subscription', customerId, subscriptionId],
      () => {
        const previousLines = previousSubscriptionValue?.Lines;
        const previousLinesWithoutDynamicBaBCount =
          previousSubscriptionValue?.Lines?.filter(({ BaBUUId }) => !BaBUUId)
            .length || 1;
        const cancelMitigationDiscountConfig =
          customerPortalSettings?.cancelMitigationDiscount;
        const newLines = previousLines?.map((line) => {
          const cancelMitigationDiscountValue =
            cancelMitigationDiscountConfig?.discountValue;
          const cancelMitigationDiscountType =
            cancelMitigationDiscountConfig?.discountType;
          const discountAmount = cancelMitigationDiscountValue
            ? cancelMitigationDiscountType === 'FIXED_AMOUNT'
              ? cancelMitigationDiscountValue
              : (+line.CurrentPriceAmount * cancelMitigationDiscountValue) / 100
            : 0;

          const amount = {
            currencyCode: line.CurrentPriceCurrencyCode,
            amount: discountAmount,
          };
          const fakeCancelMitigationDiscount = {
            amount: {
              currencyCode: amount.currencyCode,
              amount:
                cancelMitigationDiscountType === 'FIXED_AMOUNT'
                  ? (cancelMitigationDiscountValue || 0) /
                    previousLinesWithoutDynamicBaBCount
                  : discountAmount,
            },
            discount: {
              entitledLines: { all: true },
              id: new Date().toISOString(),
              recurringCycleLimit:
                cancelMitigationDiscountConfig?.discountDuration === 'ONE_TIME'
                  ? 1
                  : cancelMitigationDiscountConfig?.discountDuration ===
                    'SPECIFIC_INSTANCES'
                  ? cancelMitigationDiscountConfig?.instances
                  : undefined,
              rejectionReason: null,
              targetType: 'LINE_ITEM',
              title: 'Cancel mitigation discount',
              type: 'MANUAL',
              usageCount: 0,
              value:
                cancelMitigationDiscountType === 'PERCENTAGE'
                  ? {
                      percentage: cancelMitigationDiscountValue,
                    }
                  : { amount, appliesOnEachItem: false },
            },
          };
          const newDiscountAllocations = [
            ...(line.DiscountAllocations || []),
            fakeCancelMitigationDiscount,
          ];
          return { ...line, DiscountAllocations: newDiscountAllocations };
        });
        const { SubscriptionLevelDiscount } = getSubscriptionLevelDiscount(
          newLines as LineItem[],
        );

        const newSc = {
          ...previousSubscriptionValue,
          SubscriptionLevelDiscount,
        };

        return newSc;
      },
    );
    toast({
      isClosable: true,
      position: 'top',
      status: 'success',
      variant: 'success',
      title: t('cancelModal.discount.add_discount_success'),
    });
    onClose();
  };

  const onCancelSubscriptionMutationError = () => {
    toast({
      isClosable: true,
      position: 'top',
      status: 'error',
      title: t('cancelModal.discount.add_discount_error'),
      variant: 'error',
    });
  };
  const addDiscountMutation = useMutation(callAddDiscount, {
    onSuccess: onAddDiscountMutationSuccess,
    onSettled: () => setIsLoading(false),
    onError: onCancelSubscriptionMutationError,
  });

  const handleOnClaimDiscount = () => {
    addDiscountMutation.mutate({
      customerId,
      subscriptionId,
      authToken,
    });
  };

  return (
    <>
      <ModalHeader>{t('cancelModal.discount.title')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Box>
          <div
            dangerouslySetInnerHTML={{
              __html:
                customerPortalSettings?.cancelMitigationDiscount?.message || '',
            }}
          ></div>
          <ButtonContainer>
            <Button
              onClick={handleOnClaimDiscount}
              variant="outlinedBig"
              isLoading={isLoading}
            >
              {t('cancelModal.discount.claimDiscount')}
            </Button>
          </ButtonContainer>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button mr="4" onClick={loadPreviousFlowStep} variant="outlinedBig">
          {t('cancelModal.back')}
        </Button>
        <Button
          isLoading={isLoading}
          onClick={loadNextFlowStep}
          variant="blackBig"
        >
          {t('cancelModal.continueWithCancellation')}
        </Button>
      </ModalFooter>
    </>
  );
};

const ButtonContainer = styled.div`
  padding-top: 18px;
`;

export default DiscountAvailableModal;
